import { produce } from 'immer';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ReusableComponentsVehicleModeEnum } from '../../generated/graphql';
import { Priority } from '../../types/enums';
import { parseDate } from '../../utils/time';

export enum QueryParameterName {
  Longitude = 'lng',
  Latitude = 'lat',
  Zoom = 'z',
  ObservationDate = 'observationDate',
  RouteLabels = 'routeLabels',
  LineLabel = 'lineLabel',
  RouteId = 'routeId',
  ShowSelectedDaySituation = 'showSelectedDaySituation',
  RoutePriorities = 'routePriorities',
  TimetablesViewName = 'timetablesView',
  DayType = 'dayType',
  StartDate = 'startDate',
  EndDate = 'endDate',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Priority = 'priority',
  ReturnTo = 'returnTo',
}

export type QueryParameter<TType> = { paramName: string; value: TType };
export type QueryParameterTypes =
  | string
  | boolean
  | Temporal.PlainDate
  | number
  | undefined
  | ReadonlyArray<number>
  | ReadonlyArray<string>;
type ParameterWriteOptions = { replace?: boolean };

/**
 * Maps the query parameters into QueryParameter<QueryParameterTypes> for
 * setMultipleParametersToUrlQuery function.
 */
export function mapObjectToQueryParameterObjects<
  T extends Record<string, QueryParameterTypes>,
>(queryStringParameters: T): QueryParameter<QueryParameterTypes>[] {
  return Object.entries(queryStringParameters).map(([key, value]) => {
    return { paramName: key, value };
  });
}

export function useUrlQuery() {
  const { search: query, state: routerState } = useLocation();
  const queryParams = useMemo(
    () => qs.parse(query, { ignoreQueryPrefix: true }),
    [query],
  );

  const navigate = useNavigate();

  const setQueryString = useCallback(
    (
      queryString: string,
      replace: boolean,
      pathname?: string,
      state?: unknown,
    ) => {
      navigate(
        { search: `?${queryString}`, pathname },
        { replace, state: state ?? routerState },
      );
    },
    [navigate, routerState],
  );

  /** Sets parameter to URL query
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const setToUrlQuery = ({
    paramName,
    value,
    replace = false,
  }: {
    paramName: string;
    value: string;
    replace?: boolean;
  }) => {
    const updatedUrlQuery = produce(queryParams, (draft) => {
      draft[paramName] = value;
    });

    const queryString = qs.stringify(updatedUrlQuery);

    setQueryString(queryString, replace);
  };

  /** Sets boolean parameter to URL query
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const setBooleanToUrlQuery = (
    { paramName, value }: QueryParameter<boolean>,
    { replace }: ParameterWriteOptions = {},
  ) => {
    setToUrlQuery({ paramName, value: value.toString(), replace });
  };

  /** Sets date parameter to URL query as ISO Date
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const setDateTimeToUrlQuery = (
    { paramName, value }: QueryParameter<Temporal.PlainDate>,
    { replace }: ParameterWriteOptions = {},
  ) => {
    setToUrlQuery({ paramName, value: value.toString(), replace });
  };

  /** Sets multiple parameters to URL query
   * Converts booleans, dates and numbers in to string format
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const setMultipleParametersToUrlQuery = useCallback(
    ({
      parameters,
      replace = false,
      pathname = undefined,
      state = undefined,
    }: {
      parameters: ReadonlyArray<QueryParameter<QueryParameterTypes>>;
      replace?: boolean;
      debounced?: boolean;
      pathname?: string;
      state?: unknown;
    }) => {
      const updatedUrlQuery = produce(queryParams, (draft) => {
        parameters.forEach((parameter) => {
          if (parameter.value === undefined) {
            return;
          }

          // Convert based on the type of the value
          if (parameter.value instanceof Temporal.PlainDate) {
            draft[parameter.paramName] = parameter.value.toString();
          } else if (Array.isArray(parameter.value)) {
            draft[parameter.paramName] = parameter.value.join(',');
          } else {
            draft[parameter.paramName] = parameter.value.toString();
          }
        });
      });
      const queryString = qs.stringify(updatedUrlQuery);

      setQueryString(queryString, replace, pathname, state);
    },
    [queryParams, setQueryString],
  );

  /** Sets array parameter to URL query
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const setArrayToUrlQuery = <T>(
    { paramName, value }: QueryParameter<ReadonlyArray<T>>,
    { replace }: ParameterWriteOptions = {},
  ) => {
    setToUrlQuery({ paramName, value: value.join(','), replace });
  };

  /** Returns a query parameter in array type */
  const getArrayFromUrlQuery = (paramName: string): string[] => {
    return (queryParams[paramName] as string)
      ?.split(',')
      ?.filter((item) => item !== '');
  };

  /** Returns a query parameter in Priority array type */
  const getPriorityArrayFromUrlQuery = (
    paramName: string,
  ): Priority[] | undefined => {
    return getArrayFromUrlQuery(paramName)
      ?.map((p) => parseInt(p, 10))
      ?.filter((p) => Object.values(Priority).includes(p));
  };

  /** Returns a query parameter in TransportMode array type */
  const getTransportModeArrayFromUrlQuery = (
    paramName: string,
  ): ReusableComponentsVehicleModeEnum[] | undefined => {
    const arr = getArrayFromUrlQuery(paramName);
    if (arr && arr.length === 1 && arr[0] === 'all') {
      return undefined;
    }

    return arr
      ?.map((p) => p as ReusableComponentsVehicleModeEnum)
      ?.filter((p) =>
        Object.values(ReusableComponentsVehicleModeEnum).includes(p),
      );
  };

  /** Returns a query parameter as given enum if exists,
   * otherwise returns undefined
   */
  const getEnumFromUrlQuery = <T extends object>(
    paramName: string,
    enumType: T,
  ): T[keyof T] | undefined => {
    const paramValue = queryParams[paramName] as T[keyof T];

    return Object.values(enumType).includes(paramValue)
      ? paramValue
      : undefined;
  };

  /** Returns a query parameter in string type */
  const getStringParamFromUrlQuery = (paramName: string) => {
    return queryParams[paramName] as string | undefined;
  };

  /** Returns a query parameter in boolean type */
  const getBooleanParamFromUrlQuery = (paramName: string) => {
    return queryParams[paramName] === 'true';
  };

  /** Returns date query parameter if exists, otherwise returns undefined */
  const getDateTimeFromUrlQuery = useCallback(
    (paramName: string) => {
      return queryParams[paramName]
        ? parseDate(queryParams[paramName] as string)
        : undefined;
    },
    [queryParams],
  );

  /** Returns float query parameter if exists, otherwise returns null */
  const getFloatParamFromUrlQuery = (paramName: string) => {
    return queryParams[paramName]
      ? parseFloat(queryParams[paramName] as string)
      : null;
  };

  const getEnumArrayFromUrlQuery = <T extends object>(
    paramName: string,
    enumType: T,
  ): T[keyof T][] | undefined => {
    return getArrayFromUrlQuery(paramName)
      ?.filter((s) => s && Object.values(enumType).includes(s))
      .map((s) => s as T[keyof T]);
  };

  /** Deletes parameter from URL query
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const deleteFromUrlQuery = ({
    paramName,
    replace = false,
  }: {
    paramName: string;
    replace?: boolean;
  }) => {
    const updatedUrlQuery = produce(queryParams, (draft) => {
      delete draft[paramName];
    });

    const queryString = qs.stringify(updatedUrlQuery);

    setQueryString(queryString, replace);
  };

  /** Deletes multiple parameters from URL query
   * replace flag can be given to replace the earlier url query instead
   * of pushing it. This affects how the back button or history.back() works.
   * If the history is replaced, it means that back button will not go to the
   * url which was replaced, but rather the one before it.
   */
  const deleteMultipleFromUrlQuery = ({
    paramNames,
    replace = false,
  }: {
    paramNames: ReadonlyArray<string>;
    replace?: boolean;
  }) => {
    const updatedUrlQuery = produce(queryParams, (draft) => {
      paramNames.forEach((paramName) => {
        delete draft[paramName];
      });
    });

    const queryString = qs.stringify(updatedUrlQuery);

    setQueryString(queryString, replace);
  };

  return {
    queryParams,
    setToUrlQuery,
    setBooleanToUrlQuery,
    setDateTimeToUrlQuery,
    setArrayToUrlQuery,
    getArrayFromUrlQuery,
    getEnumArrayFromUrlQuery,
    getPriorityArrayFromUrlQuery,
    getTransportModeArrayFromUrlQuery,
    getEnumFromUrlQuery,
    getStringParamFromUrlQuery,
    getBooleanParamFromUrlQuery,
    getDateTimeFromUrlQuery,
    getFloatParamFromUrlQuery,
    setMultipleParametersToUrlQuery,
    deleteFromUrlQuery,
    deleteMultipleFromUrlQuery,
  };
}
